import React, { useState, useRef, useEffect } from 'react'
import { useAuth } from '../AuthContext'
import { signIn } from '../../lib/supabase'

function Spinner() {
  return (
    <div style={{
      width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.2)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'sg-spin 0.7s linear infinite',
      display: 'inline-block'
    }} />
  )
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

export default function SessionGuard() {
  const { sessionExpired, clearSessionExpired, user } = useAuth()

  // Store the last known email in a ref so it persists even after user is cleared
  const lastEmailRef = useRef(user?.email || '')
  useEffect(() => {
    if (user?.email) lastEmailRef.current = user.email
  }, [user])

  const [pass, setPass] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (!sessionExpired) return null

  const email = lastEmailRef.current

  async function handleSignIn(e) {
    e.preventDefault()
    if (!pass.trim()) return
    setBusy(true)
    setError('')
    try {
      await signIn(email, pass)
      clearSessionExpired()
      setPass('')
    } catch (err) {
      const msg = err?.message || ''
      if (msg.toLowerCase().includes('invalid')) {
        setError('Incorrect password. Please try again.')
      } else {
        setError(msg || 'Sign-in failed. Please try again.')
      }
    }
    setBusy(false)
  }

  const F = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: '12px 42px 12px 14px',
    fontSize: 15,
    color: '#FAF1E6',
    outline: 'none',
    fontFamily: 'var(--sans)',
    width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <div className="session-guard-modal">
      <style>{`@keyframes sg-spin { to { transform: rotate(360deg); } }`}</style>
      <div className="session-guard-card">
        {/* Logo mark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 28 }}>
          <svg width="18" height="13" viewBox="0 0 20 14" fill="none">
            {[0.3, 0.65, 1, 0.55, 0.85, 0.7, 0.4].map((h, i) => (
              <rect key={i} x={i * 2.6} y={14 - h * 14} width="2" height={h * 14} rx="1" fill="#ffa804" opacity={0.6 + h * 0.4} />
            ))}
          </svg>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#FAF1E6' }}>
            episode<span style={{ color: '#c6364a' }}>.</span>ai
          </span>
        </div>

        <div style={{ fontSize: 19, fontWeight: 700, color: '#FAF1E6', marginBottom: 8, letterSpacing: '-0.01em' }}>
          Your session has expired
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65, marginBottom: 28 }}>
          Sign back in to continue where you left off.
        </div>

        {email && (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 14,
            color: '#FAF1E6',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.45, flexShrink: 0 }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span style={{ opacity: 0.7 }}>{email}</span>
          </div>
        )}

        <form onSubmit={handleSignIn} style={{ display: 'grid', gap: 14 }}>
          <div style={{ position: 'relative' }}>
            <input
              type={showPass ? 'text' : 'password'}
              placeholder="Password"
              value={pass}
              onChange={e => setPass(e.target.value)}
              style={F}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPass(v => !v)}
              style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', padding: 0, display: 'flex' }}
            >
              <EyeIcon open={showPass} />
            </button>
          </div>

          {error && (
            <div style={{ background: 'rgba(204,51,0,0.1)', border: '1px solid rgba(204,51,0,0.25)', borderRadius: 10, padding: '9px 13px', color: '#FF7A50', fontSize: 13 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !pass.trim()}
            style={{
              border: 'none', borderRadius: 12, padding: '13px',
              background: 'linear-gradient(180deg,#c6364a,#c6364a)',
              color: '#fff', fontWeight: 700, fontSize: 15,
              cursor: busy ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: (!pass.trim() && !busy) ? 0.6 : 1,
              fontFamily: 'var(--sans)',
            }}
          >
            {busy ? <Spinner /> : 'Sign back in'}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button
            onClick={clearSessionExpired}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}
          >
            Continue as guest instead
          </button>
        </div>
      </div>
    </div>
  )
}
